import { useState } from 'react';
import { PasswordField } from '../../../shared/components/PasswordField';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { employeeOnboardingGateway } from '../index';
import { EoInput, EoSelect } from '../components/Fields';
import { SectionFrame } from '../components/SectionFrame';
import { validateBank, type BankErrors, type BankForm } from '../validation';
import type { SectionProps } from './sectionProps';

/** Shown instead of saving: the backend refuses Direct Deposit until reviewed encrypted bank storage exists. */
export const DIRECT_DEPOSIT_BLOCKER_MESSAGE = "Direct deposit can't be saved yet. Choose Check, or ask your administrator to set it up.";

const digitsOnly = (v: string, max: number) => v.replace(/\D/g, '').slice(0, max);

export function PayMethodSection({ setup, refresh, next, back }: SectionProps) {
  const [method, setMethod] = useState<string>(setup.payMethod.method ?? '');
  // Bank values live only in this component's memory while the employee is typing them. They are never sent, stored, logged or put in a URL.
  const [bank, setBank] = useState<BankForm>({ bankName: '', routingNumber: '', accountNumber: '' });
  const [bankErrors, setBankErrors] = useState<BankErrors>({});
  const [methodError, setMethodError] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setError(null);
    setMethodError(undefined);
    if (method === '') {
      setMethodError('Please choose a pay method.');
      return;
    }
    if (method === 'DirectDeposit') {
      const e = validateBank(bank);
      setBankErrors(e);
      if (Object.keys(e).length > 0) return;
      // Valid entry, but nothing may be saved: no call is made.
      setError(DIRECT_DEPOSIT_BLOCKER_MESSAGE);
      return;
    }
    setBankErrors({});
    setBusy(true);
    try {
      await employeeOnboardingGateway.saveCheckPayMethod();
      await refresh();
      next();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'We could not save your pay method. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  function clearDraft() {
    setMethod('');
    setBank({ bankName: '', routingNumber: '', accountNumber: '' });
    setBankErrors({});
    setMethodError(undefined);
    setError(null);
  }

  return (
    <SectionFrame title="Pay Method and Bank Details" onSubmit={submit} onBack={back} onClearDraft={clearDraft} busy={busy} error={error}>
      <div className="eo-grid">
        <EoSelect
          label="Pay Method"
          required
          value={method}
          error={methodError}
          onChange={(e) => {
            setMethod(e.target.value);
            setError(null);
            setMethodError(undefined);
          }}
        >
          <option value="">Select</option>
          <option value="Check">Check</option>
          <option value="DirectDeposit">Direct Deposit</option>
        </EoSelect>
      </div>

      {method === 'DirectDeposit' && (
        <>
          <p className="eo-note">Bank details are entered here only to check them. They are not stored or sent anywhere on this screen.</p>
          <div className="eo-grid">
            <EoInput
              label="Bank Name"
              required
              value={bank.bankName}
              maxLength={100}
              autoComplete="off"
              error={bankErrors.bankName}
              onChange={(e) => setBank({ ...bank, bankName: e.target.value })}
            />
            <div className="eo-field">
              <PasswordField
                label="Bank Routing Number"
                inputMode="numeric"
                autoComplete="off"
                aria-required="true"
                value={bank.routingNumber}
                errorMessage={bankErrors.routingNumber}
                onChange={(e) => setBank({ ...bank, routingNumber: digitsOnly(e.target.value, 9) })}
              />
            </div>
            <div className="eo-field">
              <PasswordField
                label="Bank Account Number"
                inputMode="numeric"
                autoComplete="off"
                aria-required="true"
                value={bank.accountNumber}
                errorMessage={bankErrors.accountNumber}
                onChange={(e) => setBank({ ...bank, accountNumber: digitsOnly(e.target.value, 17) })}
              />
            </div>
          </div>
        </>
      )}
      {method === 'Check' && <p className="eo-note">You will be paid by check. No bank details are needed.</p>}
    </SectionFrame>
  );
}
